// Sending a β report. With no connection at all, the report waits on this device (localStorage) and goes out when the
// app is online again or next opens: bugs often come with a bad connection. Other failures are shown to the tester.
import type { FeedbackRequest } from '@shared/types';
import { api, ApiError } from '../api/client';
import { safeStorage } from '../api/token';

const KEY = 'xm.fb.queue';
const MAX_QUEUE = 5;
/** localStorage holds ~5 MB per origin; leave room for everything else. */
const MAX_CHARS = 3_000_000;

function load(): FeedbackRequest[] {
  try { const v = JSON.parse(safeStorage.get('local', KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}
/** Saves the queue, dropping pictures (oldest report first) and then whole reports until it fits. */
function save(q: FeedbackRequest[]): boolean {
  let list = q.slice(-MAX_QUEUE);
  for (;;) {
    const s = JSON.stringify(list);
    if (s.length <= MAX_CHARS) {
      safeStorage.set('local', KEY, list.length ? s : null);
      return safeStorage.get('local', KEY) === (list.length ? s : null);
    }
    const i = list.findIndex((r) => r.shot || (r.photos && r.photos.length));
    if (i >= 0) list[i] = { ...list[i], shot: null, photos: [], context: { ...list[i].context, picturesDropped: 'no room on the device while offline' } };
    else if (list.length > 1) list = list.slice(1);
    else return false;
  }
}

export const queuedCount = () => load().length;

export type SendResult = { ok: true; id: string } | { ok: false; queued: true } | { ok: false; queued: false; message: string };

export async function sendFeedback(req: FeedbackRequest): Promise<SendResult> {
  try {
    const r = await api.feedback(req);
    void flushQueue();
    return { ok: true, id: r.id };
  } catch (e) {
    if (e instanceof ApiError && e.offline) {
      const q = load();
      q.push({ ...req, context: { ...req.context, queuedAt: new Date().toISOString() } });
      if (save(q)) return { ok: false, queued: true };
      return { ok: false, queued: false, message: "Can't reach Xiaoman, and there's no room to keep this here. Try again when you're online." };
    }
    return { ok: false, queued: false, message: e instanceof ApiError ? e.message : 'Could not send. Try again.' };
  }
}

let flushing = false;
/** Sends reports that waited for a connection. Stops at the first one that still can't go (offline, rate limited). */
export async function flushQueue(): Promise<number> {
  if (flushing) return 0;
  flushing = true;
  let sent = 0;
  try {
    let q = load();
    while (q.length) {
      try { await api.feedback(q[0]); sent++; } catch (e) {
        if (e instanceof ApiError && (e.offline || e.status === 429 || e.status >= 500)) break; // try again later
        // Anything else (a bad picture, ...) will never go through: drop it.
      }
      q = q.slice(1);
      save(q);
    }
  } finally { flushing = false; }
  return sent;
}
